import { forwardRef } from "react";
import InputElementTopSection from "../components/input-element-top-section/input-element-top-section";
import styles from "./select.module.less";

export type TSelectItem = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type TSelectProps = {
  label?: string;
  value: string | null;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
  isDisabled?: boolean;
  items: Array<TSelectItem>;
  onChange: (value: string | null) => void;
};

const getItemsWithAddedPlaceholder = (
  items: Array<TSelectItem>,
  placeholder: string | undefined,
): Array<TSelectItem> => {
  if (!placeholder) {
    return items;
  }

  const itemsCopy = [...items];
  itemsCopy?.unshift({ value: placeholder, label: placeholder });

  return itemsCopy;
};

const SelectBox = forwardRef<HTMLSelectElement, TSelectProps>(
  ({ value, items, placeholder, label, isDisabled, errorText, helperText, onChange }, forwardedRef) => {
    const onChangeValue = (value: string | null) => {
      if (typeof onChange !== "function") {
        return;
      }

      if (value === placeholder) {
        onChange(null);
      } else {
        onChange(value);
      }
    };

    const itemsWithPlaceholder = getItemsWithAddedPlaceholder(items, placeholder);
    const selectedItem = items.find((item) => item.value === value);
    const id = "select-" + label;

    const placeHolderDisplayed = value === null && placeholder;

    return (
      <div className={`${styles.wrapper} space-mb-md`}>
        <InputElementTopSection label={label} helperText={helperText} errorText={errorText ?? undefined} htmlFor={id} />
        <select
          ref={forwardedRef}
          className={[styles.select, errorText ? styles.error : ""].join(" ")}
          id={id}
          disabled={isDisabled}
          value={value === null ? placeholder : value}
          onChange={(e) => onChangeValue(e.target.value)}
        >
          {itemsWithPlaceholder.map((item) => (
            <option key={item.value} value={item.value} disabled={item.disabled}>
              {item.label}
            </option>
          ))}
        </select>
        <label className={[styles.label].join(" ")} htmlFor={id}>
          <div className={[styles.selectButton, placeHolderDisplayed ? styles.placeholderDisplayed : ""].join(" ")}>
            <p className={styles.triggerValue}>{value === null ? placeholder : (selectedItem?.label ?? "")}</p>
          </div>
          <div className={styles.iconWrapper}>
            <span className={["ui-icon-arrow-drop-down", styles.icon].join(" ")} />
          </div>
        </label>
      </div>
    );
  },
);

SelectBox.displayName = "SelectBox";

export default SelectBox;
